import { useEffect, useState } from 'react';
import {
  Calendar, Users, DollarSign, ShieldCheck, AlertTriangle, ArrowRight,
  IdCard, TrendingUp, Clock, Star,
} from 'lucide-react';
import { Link } from 'react-router';
import supabase from '../lib/supabase';
import { PageHeader, Section, StatCard, Badge, Button } from '../components/ui';

type Coach = {
  id: string;
  display_name: string;
  verification_status: string;
  is_active: boolean;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
};

type Stats = {
  upcoming7d: number;
  completed30d: number;
  revenue30d: number;
  avgRating: number | null;
  reviewCount: number;
};

type NextLesson = {
  id: string;
  scheduled_at: string;
  lesson_type: string;
  environment: string;
  clubs: { name: string } | null;
  profiles: { full_name: string | null } | null;
};

export default function CoachDashboard({ coachId }: { coachId: string }) {
  const [coach, setCoach] = useState<Coach | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [nextLessons, setNextLessons] = useState<NextLesson[]>([]);

  useEffect(() => {
    (async () => {
      const nowIso = new Date().toISOString();
      const [coachRes, upcomingRes, completedRes, reviewsRes, nextRes] = await Promise.all([
        supabase.from('coaches').select('*').eq('id', coachId).single(),
        supabase.from('coach_bookings')
          .select('id', { count: 'exact', head: true })
          .eq('coach_id', coachId).eq('status', 'confirmed')
          .gte('scheduled_at', nowIso)
          .lte('scheduled_at', new Date(Date.now() + 7 * 86400_000).toISOString()),
        supabase.from('coach_bookings')
          .select('coach_payout_amount', { count: 'exact' })
          .eq('coach_id', coachId)
          .in('status', ['confirmed', 'completed'])
          .gte('scheduled_at', new Date(Date.now() - 30 * 86400_000).toISOString()),
        supabase.from('reviews').select('rating').eq('coach_id', coachId),
        supabase.from('coach_bookings')
          .select('id, scheduled_at, lesson_type, environment, clubs(name), profiles!user_id(full_name)')
          .eq('coach_id', coachId).eq('status', 'confirmed')
          .gte('scheduled_at', nowIso)
          .order('scheduled_at').limit(3),
      ]);
      setCoach((coachRes.data as Coach) ?? null);
      const revenue = (completedRes.data ?? []).reduce(
        (s: number, b: any) => s + Number(b.coach_payout_amount ?? 0), 0);
      const ratings = (reviewsRes.data ?? []) as any[];
      setStats({
        upcoming7d: upcomingRes.count ?? 0,
        completed30d: completedRes.count ?? 0,
        revenue30d: revenue,
        avgRating: ratings.length ? ratings.reduce((s, r) => s + r.rating, 0) / ratings.length : null,
        reviewCount: ratings.length,
      });
      setNextLessons(((nextRes.data ?? []) as unknown as NextLesson[]) || []);
    })();
  }, [coachId]);

  const verified   = coach?.verification_status === 'verified';
  const stripeReady = !!coach?.stripe_onboarding_completed_at;
  const allGood    = verified && stripeReady && coach?.is_active;

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Coach"
        title={`Welcome, ${coach?.display_name ?? 'Coach'}`}
        subtitle={
          allGood
            ? 'You are live and taking bookings.'
            : 'Complete the steps below to start taking bookings.'
        }
      />

      {!allGood && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-amber-900">Finish your setup</div>
              <ul className="mt-3 space-y-2 text-sm text-amber-900">
                {!verified && (
                  <SetupStep to="/profile" label="Complete your profile and credentials" />
                )}
                {!stripeReady && (
                  <SetupStep to="/stripe" label="Connect Stripe so you can get paid" />
                )}
                {!coach?.is_active && verified && (
                  <SetupStep to="/profile" label="Turn on your profile once your credentials are approved" />
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Calendar}    label="Upcoming (7 days)"   value={stats?.upcoming7d} />
        <StatCard icon={TrendingUp}  label="Completed (30 days)" value={stats?.completed30d} />
        <StatCard icon={DollarSign}  label="Earnings (30 days)"
                  value={stats ? `AED ${stats.revenue30d.toFixed(0)}` : undefined} />
        <StatCard icon={Star}        label="Rating"
                  value={stats?.avgRating === null || stats?.avgRating === undefined
                    ? '—'
                    : `${stats.avgRating.toFixed(2)} · ${stats.reviewCount}`} />
      </div>

      <Section
        title={<span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#2d5016]" /> Next lessons</span>}
        subtitle="Your next three confirmed sessions."
        actions={
          <Link to="/lessons">
            <Button size="sm" variant="secondary" icon={ArrowRight}>All lessons</Button>
          </Link>
        }
        className="p-0 overflow-hidden"
      >
        {nextLessons.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Nothing booked in the next few days. Promote a package or share your profile.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {nextLessons.map((l) => (
              <li key={l.id} className="p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#c6f806]/20 text-[#2d5016] flex flex-col items-center justify-center">
                  <div className="text-[9px] font-semibold uppercase tracking-wider">
                    {new Date(l.scheduled_at).toLocaleDateString('en-GB', { month: 'short' })}
                  </div>
                  <div className="text-base font-semibold leading-none">
                    {new Date(l.scheduled_at).getDate()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">
                    {l.profiles?.full_name ?? 'AceRent player'}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 capitalize flex-wrap">
                    {new Date(l.scheduled_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    <Badge tone="lime">{l.lesson_type.replace('_', ' ')}</Badge>
                    <span>· {l.environment}</span>
                    {l.clubs?.name && <span>· {l.clubs.name}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={<span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2d5016]" /> Account status</span>}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatusPill
            icon={IdCard}
            label="Verification"
            ok={verified}
            value={coach?.verification_status?.replace('_', ' ') ?? '—'}
          />
          <StatusPill
            icon={DollarSign}
            label="Stripe payouts"
            ok={stripeReady}
            value={stripeReady ? 'Connected' : 'Not connected'}
          />
          <StatusPill
            icon={Users}
            label="Visibility"
            ok={!!coach?.is_active}
            value={coach?.is_active ? 'Live on customer app' : 'Hidden from customers'}
          />
        </div>
      </Section>
    </div>
  );
}

function SetupStep({ to, label }: { to: string; label: string }) {
  return (
    <li className="flex items-center justify-between gap-2 bg-white rounded-xl px-3 py-2 border border-amber-200">
      <span>{label}</span>
      <Link to={to} className="text-[#2d5016] hover:underline text-xs font-medium flex items-center gap-1">
        Go <ArrowRight className="w-3 h-3" />
      </Link>
    </li>
  );
}

function StatusPill({
  icon: Icon, label, ok, value,
}: { icon: any; label: string; ok: boolean; value: string }) {
  return (
    <div className={`rounded-xl p-4 border ${ok
      ? 'bg-green-50 border-green-200'
      : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className={`text-sm font-medium mt-1 capitalize ${ok ? 'text-green-800' : 'text-gray-700'}`}>
        {value}
      </div>
    </div>
  );
}
